import { GatewayControleQuinquennal } from "@conformite/gateway";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { ControleQuinquennalApi } from "../controle-quinquennal.api";
import { controleQuinquennalKeys } from "./controle-quinquennal.keys";

// Fréquence de polling tant qu'une analyse IA est en cours (statut PENDING).
const PENDING_POLL_INTERVAL_MS = 5_000;
// Borne le polling au temps écoulé depuis l'entrée en PENDING (pas `dataUpdateCount`,
// compteur de vie qui ne se réinitialise jamais). ~10 min.
const MAX_PENDING_POLL_DURATION_MS = 10 * 60 * 1000;

export const useCQAxeWithFilesPaginatedQuery = (
  idControleQuinquennal: string,
  idAxe: string,
  search?: string,
  limit: number = 20
) => {
  // Horodatage de l'entrée en PENDING, remis à zéro dès que l'axe en sort.
  const pendingSinceRef = useRef<number | null>(null);
  return useQuery({
    queryKey: controleQuinquennalKeys.axesWithFiles(
      idControleQuinquennal,
      idAxe,
      search,
      limit
    ),
    queryFn: () =>
      ControleQuinquennalApi.listCQAxeWithFilesPaginated(
        idControleQuinquennal,
        idAxe,
        search,
        limit
      ),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    enabled: !!idControleQuinquennal && !!idAxe,
    // Suit la fin de l'analyse relancée : on rafraîchit tant que l'axe est
    // PENDING, mais borné pour ne pas boucler à l'infini sur un PENDING orphelin.
    refetchInterval: (query) => {
      const isPending =
        query.state.data?.axe?.processingStatus ===
        GatewayControleQuinquennal.VerificationStatus.PENDING;
      if (!isPending) {
        pendingSinceRef.current = null;
        return false;
      }
      const now = Date.now();
      if (pendingSinceRef.current === null) {
        pendingSinceRef.current = now;
      }
      if (now - pendingSinceRef.current > MAX_PENDING_POLL_DURATION_MS) {
        return false;
      }
      return PENDING_POLL_INTERVAL_MS;
    },
  });
};
